// weekly_planner.ts — Інтерактивний timeline планувальник тижневого графіка v2
// Dual view: Week Overview ↔ Day Timeline с drag & drop, resize, split, find slot

export interface PlannerTask {
    id: string;
    parentId?: string; // Для розділених задач — ID батьківської
    dayIndex: number; // 0=Пн..6=Нд, -1=Пул боргу
    startTime: string; // '18:30'
    endTime: string; // '22:30'
    durationMins: number; // 240
    title: string;
    category: 'delivery' | 'school' | 'pum' | 'sleep' | 'commute' | 'meal' | 'routine' | 'sport' | 'other';
    isFixed: boolean;
    allowSplit: boolean;
    minStart?: string;
    maxEnd?: string;
    priority: 'high' | 'medium' | 'low';
    status: 'planned' | 'in_progress' | 'completed' | 'missed' | 'needs_reschedule';
    notes?: string;
    weatherAlert?: string;
}

export interface FreeSlot {
    dayIndex: number;
    startMins: number;
    endMins: number;
    startTime: string;
    endTime: string;
    durationMins: number;
}

export interface CandidateSlot {
    dayIndex: number;
    dayName: string;
    startTime: string;
    endTime: string;
    durationMins: number;
    isPeak: boolean;
    isBuffer: boolean;
    gapDurationMins: number;
    description: string;
}

const DAYS_UK = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', "П'ятниця", 'Субота', 'Неділя'];
const DAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

const PX_PER_MIN = 1.05; // 1 хвилина = 1.05px висоти на timeline (компактний зручний масштаб)
const TIMELINE_START_HOUR = 8; // Робочий день починається з 08:00 (сон 00:00-09:00 закріплений зверху)
const TIMELINE_END_HOUR = 24;
const SNAP_MINS = 15; // Snap до 15 хвилин
const TIMELINE_BOTTOM_PAD_PX = 20; // Відступ знизу після 24:00

let plannerSettings = {
    saturdayPeakStart: '17:00',
    saturdayPeakEnd: '22:00',
    deliveryWeeklyQuota: 25.0
};

// Стан планувальника
let tasks: PlannerTask[] = [];
let currentView: 'week' | 'day' = 'day'; // За замовчуванням відкриваємо день (фокус на сьогодні)
let selectedDayIndex: number = -1; // Обраний день для detail view
let draggedTaskId: string | null = null;
let isDraggingOnTimeline = false;
let isResizing = false;
let resizeEdge: 'top' | 'bottom' | null = null;
let resizeTaskId: string | null = null;
let resizeStartY = 0;
let resizeOriginalMins = 0;
let dragStartY = 0;
let dragOriginalStartMins = 0;

let activeWeatherAlert: { dayIndex: number; text: string; taskTitle: string } | null = null;

// Реальний прогноз (Open-Meteo, без API-ключа). Локація: м. Луцьк, Волинська обл.
const WEATHER_LAT = 50.7472;
const WEATHER_LON = 25.3254;

async function loadWeatherAlert() {
    try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${WEATHER_LAT}&longitude=${WEATHER_LON}`
            + `&hourly=precipitation_probability,precipitation,wind_gusts_10m&wind_speed_unit=ms&timezone=Europe%2FKyiv&forecast_days=7`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const h = data.hourly;
        const todayIdx = (new Date().getDay() + 6) % 7;
        const todayStr = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Kyiv' });

        let alert: typeof activeWeatherAlert = null;
        // Перевіряємо дні до кінця тижня, лише години запланованої доставки
        for (let i = 0; i < h.time.length && !alert; i++) {
            const [date, time] = String(h.time[i]).split('T');
            const diffDays = Math.round((Date.parse(date) - Date.parse(todayStr)) / 86400000);
            const dayIndex = todayIdx + diffDays;
            if (diffDays < 0 || dayIndex > 6) continue;

            const hourMins = timeToMins(time);
            const delivery = tasks.find(t => t.dayIndex === dayIndex && t.category === 'delivery' && t.status !== 'needs_reschedule'
                && hourMins >= timeToMins(t.startTime) && hourMins < timeToMins(t.endTime));
            if (!delivery) continue;

            const prob = h.precipitation_probability[i] ?? 0;
            const mm = h.precipitation[i] ?? 0;
            const gust = h.wind_gusts_10m[i] ?? 0;
            const rain = prob >= 60 && mm >= 1;
            const wind = gust >= 14;
            if (rain || wind) {
                const parts: string[] = [];
                if (rain) parts.push(`дощ ${mm.toFixed(1)} мм (${prob}%)`);
                if (wind) parts.push(`пориви вітру до ${Math.round(gust)} м/с`);
                alert = { dayIndex, taskTitle: delivery.title, text: `близько ${time}: ${parts.join(', ')}. Доставка під ризиком.` };
            }
        }

        const changed = JSON.stringify(alert) !== JSON.stringify(activeWeatherAlert);
        activeWeatherAlert = alert;
        if (changed) renderWeeklyPlanner();
    } catch (e) {
        console.warn('Weather forecast unavailable', e);
        if (activeWeatherAlert) { activeWeatherAlert = null; renderWeeklyPlanner(); }
    }
}

// ==================== УТИЛІТИ ====================
function timeToMins(timeStr: string): number {
    if (!timeStr) return 0;
    if (timeStr === '24:00' || timeStr === '00:00_end') return 1440;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

function minsToTime(mins: number): string {
    const clamped = Math.max(0, Math.min(1440, mins));
    if (clamped === 1440) return '24:00';
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDuration(mins: number): string {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h} год ${m} хв`;
    if (h > 0) return `${h} год`;
    return `${m} хв`;
}

function snapToGrid(mins: number): number {
    return Math.round(mins / SNAP_MINS) * SNAP_MINS;
}

function minsToY(mins: number): number {
    return (mins - TIMELINE_START_HOUR * 60) * PX_PER_MIN;
}

function yToMins(y: number): number {
    return snapToGrid(y / PX_PER_MIN + TIMELINE_START_HOUR * 60);
}

function getCategoryColor(cat: string): string {
    const map: Record<string, string> = {
        delivery: '#f97316', school: '#f59e0b', pum: '#0ea5e9',
        sleep: '#8b5cf6', commute: '#64748b', meal: '#10b981',
        routine: '#3b82f6', sport: '#a855f7', other: '#71717a'
    };
    return map[cat] || '#71717a';
}

function getCategoryIcon(cat: string): string {
    const map: Record<string, string> = {
        delivery: 'ri-riding-line', school: 'ri-book-read-line', pum: 'ri-macbook-line',
        sleep: 'ri-moon-clear-line', commute: 'ri-walk-line', meal: 'ri-restaurant-line',
        routine: 'ri-home-4-line', sport: 'ri-run-line', other: 'ri-task-line'
    };
    return map[cat] || 'ri-task-line';
}

// ==================== БІЗНЕС-ЛОГІКА ====================
export function createDefaultSchedule(): PlannerTask[] {
    const result: PlannerTask[] = [];

    for (let day = 0; day < 5; day++) {
        result.push(
            { id: `sleep_${day}`, dayIndex: day, startTime: '00:00', endTime: '09:00', durationMins: 540, title: 'Сон (Залізне вікно)', category: 'sleep', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
            { id: `routine_morning_${day}`, dayIndex: day, startTime: '09:00', endTime: '10:30', durationMins: 90, title: 'Ранок, побут & сніданок', category: 'routine', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `commute_pum_${day}`, dayIndex: day, startTime: '10:30', endTime: '11:00', durationMins: 30, title: 'Дорога до ПУМ', category: 'commute', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `pum_${day}`, dayIndex: day, startTime: '11:00', endTime: '12:30', durationMins: 90, title: 'ПУМ (технік)', category: 'pum', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
            { id: `lunch_${day}`, dayIndex: day, startTime: '12:30', endTime: '13:00', durationMins: 30, title: 'Обід & перепочинок', category: 'meal', isFixed: false, allowSplit: false, priority: 'low', status: 'planned' },
            { id: `commute_school_${day}`, dayIndex: day, startTime: '13:00', endTime: '13:30', durationMins: 30, title: 'Дорога до ліцею', category: 'commute', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `school_${day}`, dayIndex: day, startTime: '13:30', endTime: '18:00', durationMins: 270, title: 'Школа / Ліцей (інклюзія)', category: 'school', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
            { id: `commute_delivery_${day}`, dayIndex: day, startTime: '18:00', endTime: '18:30', durationMins: 30, title: 'Перехід на точку замовлень', category: 'commute', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `delivery_${day}`, dayIndex: day, startTime: '18:30', endTime: '22:30', durationMins: 240, title: 'Доставка (Glovo + Bolt)', category: 'delivery', isFixed: false, allowSplit: true, minStart: '17:00', maxEnd: '23:00', priority: 'medium', status: 'planned' },
            { id: `dinner_evening_${day}`, dayIndex: day, startTime: '22:30', endTime: '24:00', durationMins: 90, title: 'Вечеря, душ, особистий час', category: 'routine', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' }
        );
    }

    result.push(
        { id: 'sleep_sat', dayIndex: 5, startTime: '00:00', endTime: '09:00', durationMins: 540, title: 'Сон (Залізне вікно)', category: 'sleep', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
        { id: 'delivery_sat', dayIndex: 5, startTime: '17:00', endTime: '22:00', durationMins: 300, title: 'Доставка (Glovo + Bolt)', category: 'delivery', isFixed: false, allowSplit: true, minStart: '12:00', maxEnd: '23:00', priority: 'medium', status: 'planned', notes: 'Суботній піковий блок замовлень (5 год)' },
        { id: 'dinner_sat', dayIndex: 5, startTime: '22:00', endTime: '24:00', durationMins: 120, title: 'Вечеря, відпочинок, вільний час', category: 'routine', isFixed: false, allowSplit: false, priority: 'low', status: 'planned' }
    );

    result.push(
        { id: 'sleep_sun', dayIndex: 6, startTime: '00:00', endTime: '09:00', durationMins: 540, title: 'Сон (Залізне вікно)', category: 'sleep', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' }
    );

    return result;
}

export function getFreeSlotsForDay(dayIndex: number): FreeSlot[] {
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIndex && t.status !== 'needs_reschedule')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

    const freeSlots: FreeSlot[] = [];
    let currentMins = 0;

    for (const task of dayTasks) {
        const taskStart = timeToMins(task.startTime);
        const taskEnd = timeToMins(task.endTime);
        if (taskStart > currentMins) {
            const gap = taskStart - currentMins;
            if (gap >= 15) {
                freeSlots.push({ dayIndex, startMins: currentMins, endMins: taskStart, startTime: minsToTime(currentMins), endTime: minsToTime(taskStart), durationMins: gap });
            }
        }
        currentMins = Math.max(currentMins, taskEnd);
    }

    if (currentMins < 1440) {
        const gap = 1440 - currentMins;
        if (gap >= 15) {
            freeSlots.push({ dayIndex, startMins: currentMins, endMins: 1440, startTime: minsToTime(currentMins), endTime: '24:00', durationMins: gap });
        }
    }

    return freeSlots;
}

export function canPlaceTask(dayIndex: number, startMins: number, durationMins: number, excludeTaskId: string | null = null): { ok: boolean; reason?: string } {
    const endMins = startMins + durationMins;
    if (endMins > 1440) return { ok: false, reason: 'Задача виходить за межі доби (24:00)' };

    const dayTasks = tasks.filter(t => t.dayIndex === dayIndex && t.id !== excludeTaskId && t.status !== 'needs_reschedule');

    for (const other of dayTasks) {
        const otherStart = timeToMins(other.startTime);
        const otherEnd = timeToMins(other.endTime);
        if (startMins < otherEnd && endMins > otherStart) {
            return { ok: false, reason: `Конфлікт із «${other.title}» (${other.startTime}–${other.endTime})` };
        }
    }

    return { ok: true };
}

export function findAvailableSlots(task: PlannerTask): CandidateSlot[] {
    const candidates: CandidateSlot[] = [];
    const duration = task.durationMins;

    for (let day = 0; day < 7; day++) {
        const freeSlots = getFreeSlotsForDay(day);

        for (const slot of freeSlots) {
            let usableStart = Math.max(slot.startMins, 540); // Від 09:00
            let usableEnd = slot.endMins;

            if (usableEnd - usableStart >= duration) {
                const check = canPlaceTask(day, usableStart, duration, task.id);
                if (check.ok) {
                    const isPeak = day === 5 && task.category === 'delivery' && usableStart >= timeToMins(plannerSettings.saturdayPeakStart);
                    const isBuffer = day === 6;
                    candidates.push({
                        dayIndex: day,
                        dayName: DAYS_UK[day],
                        startTime: minsToTime(usableStart),
                        endTime: minsToTime(usableStart + duration),
                        durationMins: duration,
                        isPeak,
                        isBuffer,
                        gapDurationMins: slot.durationMins,
                        description: isPeak ? '⭐ Пікові години доставки' : isBuffer ? '🌿 Буфер відновлення' : `Вільне вікно на ${formatDuration(slot.durationMins)}`
                    });
                }
            }
        }
    }

    const seen = new Set<string>();
    return candidates.filter(c => {
        const key = `${c.dayIndex}_${c.startTime}_${c.endTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// ==================== ЗБЕРЕЖЕННЯ / ЗАВАНТАЖЕННЯ ====================
function jsonParseSafe(str: string): any {
    try { return JSON.parse(str); } catch { return null; }
}

export async function loadScheduleData() {
    try {
        const res = await fetch('api/slots.php?action=get_schedule');
        if (res.ok) {
            const data = await res.json();
            if (data.success && data.schedule && Array.isArray(data.schedule.tasks)) {
                tasks = data.schedule.tasks;
                if (data.schedule.settings) plannerSettings = { ...plannerSettings, ...data.schedule.settings };
                renderWeeklyPlanner();
                if (typeof (window as any).syncDeliveryWidgetFromPlanner === 'function') {
                    (window as any).syncDeliveryWidgetFromPlanner();
                }
                return;
            }
        }
    } catch (e) {
        console.warn('API get_schedule failed, using local storage or template', e);
    }

    const saved = localStorage.getItem('syncflow_weekly_schedule_v2');
    if (saved) {
        try {
            const parsed = jsonParseSafe(saved);
            if (parsed && Array.isArray(parsed.tasks)) {
                tasks = parsed.tasks;
                renderWeeklyPlanner();
                return;
            }
        } catch (e) { /* fallback */ }
    }

    tasks = createDefaultSchedule();
    saveScheduleDataLocally();
    renderWeeklyPlanner();
}

export async function saveScheduleData() {
    saveScheduleDataLocally();
    if (typeof (window as any).syncDeliveryWidgetFromPlanner === 'function') {
        (window as any).syncDeliveryWidgetFromPlanner();
    }
    try {
        await fetch('api/slots.php?action=save_schedule', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ schedule: { tasks, settings: plannerSettings, updated_at: new Date().toISOString() } })
        });
    } catch (e) {
        console.error('Failed to sync schedule with server', e);
    }
}

export function getPlannedDeliveryHours(): number {
    const deliveryTasks = tasks.filter(t => t.category === 'delivery' && t.dayIndex >= 0 && t.status !== 'needs_reschedule');
    const totalMins = deliveryTasks.reduce((sum, t) => sum + t.durationMins, 0);
    return Number((totalMins / 60).toFixed(1));
}
(window as any).getPlannedDeliveryHours = getPlannedDeliveryHours;

function saveScheduleDataLocally() {
    localStorage.setItem('syncflow_weekly_schedule_v2', JSON.stringify({ tasks, settings: plannerSettings }));
}

// ==================== СТАТИСТИКА ====================
function getDayStats(dayIndex: number) {
    const dayTasks = tasks.filter(t => t.dayIndex === dayIndex && t.status !== 'needs_reschedule');
    let workMins = 0, commuteMins = 0, routineMins = 0, sleepMins = 0;

    for (const t of dayTasks) {
        if (t.category === 'school' || t.category === 'pum' || t.category === 'delivery') workMins += t.durationMins;
        else if (t.category === 'commute') commuteMins += t.durationMins;
        else if (t.category === 'routine' || t.category === 'meal' || t.category === 'sport') routineMins += t.durationMins;
        else if (t.category === 'sleep') sleepMins += t.durationMins;
    }

    const scheduledMins = workMins + commuteMins + routineMins + sleepMins;
    const freeMins = Math.max(0, 1440 - scheduledMins);

    return {
        workMins, commuteMins, routineMins, sleepMins, scheduledMins, freeMins,
        workH: (workMins / 60).toFixed(1), commuteH: (commuteMins / 60).toFixed(1),
        routineH: (routineMins / 60).toFixed(1), sleepH: (sleepMins / 60).toFixed(1),
        freeH: (freeMins / 60).toFixed(1), scheduledH: (scheduledMins / 60).toFixed(1),
        taskCount: dayTasks.length
    };
}

function getWeekDeliverySummary() {
    const deliveryTasks = tasks.filter(t => t.category === 'delivery');
    let plannedHours = 0, completedHours = 0, debtHours = 0;

    for (const t of deliveryTasks) {
        const hours = t.durationMins / 60;
        if (t.dayIndex === -1 || t.status === 'needs_reschedule' || t.status === 'missed') debtHours += hours;
        else if (t.status === 'completed') { completedHours += hours; plannedHours += hours; }
        else plannedHours += hours;
    }

    return { planned: plannedHours, completed: completedHours, debt: debtHours, quota: plannerSettings.deliveryWeeklyQuota };
}

// ==================== ГОЛОВНИЙ РЕНДЕР ====================
export function renderWeeklyPlanner() {
    const container = document.getElementById('weekly-planner-root');
    if (!container) return;

    const todayDayIndex = (new Date().getDay() + 6) % 7;
    const unassignedTasks = tasks.filter(t => t.dayIndex === -1 || t.status === 'needs_reschedule');

    // Auto-select today if nothing selected
    if (selectedDayIndex < 0) selectedDayIndex = todayDayIndex;

    let html = `<div class="wp2-container">`;

    // Top bar
    html += renderTopBar(unassignedTasks.length);

    // Weather alert
    if (activeWeatherAlert) {
        html += `
        <div class="wp2-alert">
            <div class="wp2-alert-text">
                <i class="ri-rainy-line" style="font-size:18px;color:#38bdf8;"></i>
                <span><strong>Погода на ${DAYS_UK[activeWeatherAlert.dayIndex]}:</strong> ${activeWeatherAlert.text}</span>
            </div>
            <button class="wp2-btn wp2-btn-danger" onclick="rescheduleWeatherTask(${activeWeatherAlert.dayIndex})">
                <i class="ri-arrow-right-line"></i> Перенести доставку
            </button>
        </div>`;
    }

    // Debt pool
    if (unassignedTasks.length > 0) {
        html += renderDebtPool(unassignedTasks);
    }

    // Day detail view (or week grid)
    if (currentView === 'day') {
        html += renderWeekDayStrip(todayDayIndex);
        html += `<div id="wp2-day-detail-slot">${renderDayDetail(selectedDayIndex, todayDayIndex)}</div>`;
    } else {
        html += renderWeekGrid(todayDayIndex);
    }

    html += `</div>`;

    // Зберігаємо поточний скрол таймлайну, якщо він уже існував
    const oldScrollTop = document.getElementById('wp2-timeline-wrapper')?.scrollTop;

    container.innerHTML = html;

    // Initialize interactions
    if (currentView === 'day') {
        setupTimelineDragAndDrop();
        setupTimelineResize();
        if (oldScrollTop !== undefined && oldScrollTop > 0) {
            const wrapper = document.getElementById('wp2-timeline-wrapper');
            if (wrapper) wrapper.scrollTop = oldScrollTop;
        } else {
            scrollToCurrentTime(true);
        }
    }
    setupWeekDragDrop();
    requestAnimationFrame(() => {
        syncTimelineHeightWithSidebar();
    });
}

function renderTopBar(debtCount: number): string {
    return `
    <div class="wp2-topbar">
        <div class="wp2-topbar-left">
            <span class="wp2-title">
                <i class="ri-calendar-schedule-line" style="color:var(--brand-primary,#3b82f6)"></i>
                Часові Блоки Дня
            </span>
            <div class="wp2-view-toggle">
                <button class="wp2-view-btn ${currentView === 'day' ? 'active' : ''}" onclick="setPlannerView('day')">
                    <i class="ri-layout-column-line"></i> День
                </button>
                <button class="wp2-view-btn ${currentView === 'week' ? 'active' : ''}" onclick="setPlannerView('week')">
                    <i class="ri-layout-grid-line"></i> Тиждень
                </button>
            </div>
            ${debtCount > 0 ? `<span class="wp2-debt-badge"><i class="ri-alarm-warning-fill"></i> Борг: ${debtCount}</span>` : ''}
        </div>
        <div class="wp2-topbar-right">
            <button class="wp2-btn" onclick="applyBaseScheduleTemplate()" data-tooltip="Застосувати базовий тижневий шаблон" data-tooltip-pos="bottom">
                <i class="ri-history-line"></i> Шаблон
            </button>
            <button class="wp2-btn wp2-btn-primary" onclick="openAddTaskModal()">
                <i class="ri-add-line"></i> Додати задачу
            </button>
        </div>
    </div>`;
}

function renderDebtPool(unassigned: PlannerTask[]): string {
    return `
    <div class="wp2-debt-pool">
        <div class="wp2-debt-header">
            <span class="wp2-debt-title">
                <i class="ri-alarm-warning-fill"></i> Потребують перенесення (${unassigned.length})
            </span>
            <button class="wp2-btn wp2-btn-sm wp2-btn-magic" onclick="autoRescheduleDebtTasks()" data-tooltip="Автоматично розмістити у найближчі вільні слоти" data-tooltip-pos="bottom">
                <i class="ri-magic-line"></i> Авто-розміщення
            </button>
        </div>
        <div class="wp2-debt-cards">
            ${unassigned.map(t => `
                <div class="wp2-debt-card" draggable="true" data-task-id="${t.id}">
                    <div class="wp2-debt-card-title">
                        <i class="${getCategoryIcon(t.category)}" style="color:${getCategoryColor(t.category)}"></i>
                        <span>${t.title}</span>
                    </div>
                    <div class="wp2-debt-card-meta">
                        <i class="ri-time-line"></i> ${formatDuration(t.durationMins)}
                    </div>
                    <div class="wp2-debt-card-actions">
                        <button class="wp2-btn wp2-btn-sm" onclick="openFindSlotModal('${t.id}')"><i class="ri-search-eye-line"></i> Знайти слот</button>
                        ${t.allowSplit || t.durationMins >= 120 ? `<button class="wp2-btn wp2-btn-sm" onclick="openSplitTaskModal('${t.id}')" title="Розділити навпіл"><i class="ri-scissors-cut-line"></i></button>` : ''}
                        <button class="wp2-btn wp2-btn-sm wp2-btn-danger" onclick="dismissDebtTask('${t.id}')"><i class="ri-close-line"></i></button>
                    </div>
                </div>
            `).join('')}
        </div>
    </div>`;
}

// ==================== WEEK DAY STRIP ====================
function renderWeekDayStrip(todayIdx: number): string {
    let html = `<div class="wp2-day-strip" id="wp2-day-strip">`;

    for (let day = 0; day < 7; day++) {
        const stats = getDayStats(day);
        const isToday = day === todayIdx;
        const isSelected = currentView === 'day' && day === selectedDayIndex;
        const fillPct = Math.min(100, Math.round((Number(stats.scheduledH) / 15) * 100));
        const badge = isToday
            ? '<span class="wp2-strip-badge-today">Сьогодні</span>'
            : (day === 6 ? '<span class="wp2-strip-badge-weekend">Вихідний</span>' : '');

        html += `
        <div class="wp2-day-strip-item ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}" 
             data-day-index="${day}" 
             onclick="selectDay(${day})"
             id="wp2-strip-day-${day}"
             data-tooltip="${DAYS_UK[day]}: заплановано ${stats.scheduledH} год, вільно ${stats.freeH} год"
             data-tooltip-pos="bottom">
            <div class="wp2-strip-header">
                <span class="wp2-strip-name">${DAYS_SHORT[day]}</span>
                ${badge}
            </div>
            <div class="wp2-strip-body">
                <span class="wp2-strip-hours">${stats.scheduledH} год</span>
                <span class="wp2-strip-free">+${stats.freeH}г</span>
            </div>
            <div class="wp2-strip-progress">
                <div class="wp2-strip-progress-bar" style="width:${fillPct}%"></div>
            </div>
        </div>`;
    }

    html += `</div>`;
    return html;
}

// ==================== WEEK GRID (ALTERNATIVE VIEW) ====================
function renderWeekGrid(todayIdx: number): string {
    let html = `<div class="wp2-week-grid" id="wp2-week-grid">`;

    for (let day = 0; day < 7; day++) {
        const stats = getDayStats(day);
        const isToday = day === todayIdx;
        const dayTasks = tasks
            .filter(t => t.dayIndex === day && t.status !== 'needs_reschedule' && t.category !== 'sleep')
            .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

        const fillPct = Math.min(100, Math.round((Number(stats.scheduledH) / 15) * 100));
        const badge = isToday
            ? '<span class="wp2-strip-badge-today">Сьогодні</span>'
            : (day === 6 ? '<span class="wp2-strip-badge-weekend">Вихідний</span>' : '');

        html += `
        <div class="wp2-grid-col ${isToday ? 'is-today' : ''}" data-day-index="${day}">
            <div class="wp2-grid-col-header" onclick="selectDay(${day})" 
                 data-tooltip="${DAYS_UK[day]}: заплановано ${stats.scheduledH} год, вільно ${stats.freeH} год"
                 data-tooltip-pos="bottom">
                <div class="wp2-grid-header-top">
                    <div class="wp2-grid-header-left">
                        <span class="wp2-grid-day-name">${DAYS_SHORT[day]}</span>
                        ${badge}
                    </div>
                    <div class="wp2-grid-header-right">
                        <span class="wp2-grid-day-hours">${stats.scheduledH} год</span>
                        <span class="wp2-strip-free">+${stats.freeH}г</span>
                    </div>
                </div>
                <div class="wp2-grid-progress">
                    <div class="wp2-grid-progress-bar" style="width:${fillPct}%"></div>
                </div>
            </div>
            <div class="wp2-grid-col-body">
                ${dayTasks.map(t => {
                    const color = getCategoryColor(t.category);
                    return `
                    <div class="wp2-grid-task ${t.isFixed ? 'is-fixed' : ''}" draggable="${!t.isFixed}" data-task-id="${t.id}" style="border-left:3px solid ${color};background:${color}15;" onclick="selectDay(${day})" title="${t.title} (${t.startTime}–${t.endTime})">
                        <div class="wp2-grid-task-header">
                            <div class="wp2-grid-task-time">${t.startTime}–${t.endTime}</div>
                            ${!t.isFixed ? `<button type="button" class="wp2-grid-task-del" onclick="event.stopPropagation();deletePlannerTask('${t.id}')" title="Видалити слот"><i class="ri-close-line"></i></button>` : ''}
                        </div>
                        <div class="wp2-grid-task-title">${t.title}</div>
                    </div>`;
                }).join('')}
                <button class="wp2-grid-add-btn" onclick="openAddTaskModal();">+ Додати</button>
            </div>
        </div>`;
    }

    html += `</div>`;
    return html;
}

// ==================== DAY DETAIL VIEW ====================
function renderDayHeaderContent(dayIdx: number, todayIdx: number): string {
    const stats = getDayStats(dayIdx);
    const isToday = dayIdx === todayIdx;
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIdx && t.status !== 'needs_reschedule' && t.category !== 'sleep')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

    return `
        <div class="wp2-day-header-left">
            <div class="wp2-day-nav-group">
                <button type="button" class="wp2-day-nav-btn" onclick="navigateDay(-1)" data-tooltip="Попередній день" data-tooltip-pos="bottom">
                    <i class="ri-arrow-left-s-line"></i>
                </button>
                <button type="button" class="wp2-day-nav-btn" onclick="navigateDay(1)" data-tooltip="Наступний день" data-tooltip-pos="bottom">
                    <i class="ri-arrow-right-s-line"></i>
                </button>
            </div>
            <span class="wp2-day-name-big">${DAYS_UK[dayIdx]}</span>
            ${isToday ? '<span class="wp2-day-badge-today">Сьогодні</span>' : `<button type="button" class="wp2-day-btn-today" onclick="selectDay(${todayIdx}, true)" data-tooltip="Повернутися на сьогодні" data-tooltip-pos="bottom"><i class="ri-calendar-check-line"></i> Сьогодні</button>`}
        </div>
        <div class="wp2-day-header-stats">
            <span class="wp2-day-header-stat" data-tooltip="Заплановано на день: ${stats.scheduledH} год" data-tooltip-pos="bottom">
                <span class="wp2-stat-lbl">План:</span> <strong>${stats.scheduledH}г</strong>
            </span>
            <span class="wp2-day-header-stat stat-free" data-tooltip="Вільного часу: ${stats.freeH} год" data-tooltip-pos="bottom">
                <span class="wp2-stat-lbl">Вільно:</span> <strong style="color:var(--color-success,#10b981)">${stats.freeH}г</strong>
            </span>
            <span class="wp2-day-header-stat stat-blocks" data-tooltip="Кількість часових блоків: ${dayTasks.length}" data-tooltip-pos="bottom">
                <span class="wp2-stat-lbl">Блоків:</span> <strong>${dayTasks.length}</strong>
            </span>
        </div>
    `;
}

function renderDayTimelineInner(dayIdx: number, todayIdx: number): string {
    const isToday = dayIdx === todayIdx;
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIdx && t.status !== 'needs_reschedule' && t.category !== 'sleep')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));
    const freeSlots = getFreeSlotsForDay(dayIdx).filter(s => s.endMins > TIMELINE_START_HOUR * 60);

    return `
        ${renderHourLines()}
        ${isToday ? renderNowLine() : ''}
        ${freeSlots.map(s => renderFreeBlock(s)).join('')}
        ${dayTasks.map(t => renderTaskBlock(t)).join('')}
    `;
}

function renderDayDetail(dayIdx: number, todayIdx: number): string {
    return `
    <div class="wp2-day-detail-fullscreen">
        <!-- Sleep Rule Banner -->
        <div class="wp2-sleep-banner">
            <div class="wp2-sleep-banner-left">
                <div class="wp2-sleep-icon"><i class="ri-moon-clear-line"></i></div>
                <div>
                    <span class="wp2-sleep-title">Залізне вікно сну (недоторканне)</span>
                    <span class="wp2-sleep-desc">00:00 – 09:00 (9.0 год відновлення) · Жодних компромісів зі сном</span>
                </div>
            </div>
            <div class="wp2-sleep-banner-right">
                <span class="wp2-sleep-badge"><i class="ri-shield-check-line"></i> 100% Захищено</span>
            </div>
        </div>

        <div class="wp2-day-card">
            <div class="wp2-day-header" id="wp2-day-header">
                ${renderDayHeaderContent(dayIdx, todayIdx)}
            </div>

            <div class="wp2-timeline-wrapper" id="wp2-timeline-wrapper">
                <div style="position:relative;height:${(TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60 * PX_PER_MIN + TIMELINE_BOTTOM_PAD_PX}px">
                    <!-- Hour labels and grid lines -->
                    <div class="wp2-timeline-hours">
                        ${renderHourLabels()}
                    </div>

                    <!-- Timeline area -->
                    <div class="wp2-timeline" id="wp2-timeline" data-day-index="${dayIdx}">
                        ${renderDayTimelineInner(dayIdx, todayIdx)}
                    </div>
                </div>
            </div>
        </div>
    </div>`;
}

function renderHourLabels(): string {
    let html = '';
    for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
        const y = (h - TIMELINE_START_HOUR) * 60 * PX_PER_MIN;
        const isFirst = h === TIMELINE_START_HOUR;
        const style = isFirst ? `top:${y + 4}px;transform:none;` : `top:${y}px;`;
        html += `<div class="wp2-hour-label" style="${style}">${String(h).padStart(2, '0')}:00</div>`;
    }
    return html;
}

function renderHourLines(): string {
    let html = '';
    for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
        const y = (h - TIMELINE_START_HOUR) * 60 * PX_PER_MIN;
        if (h > TIMELINE_START_HOUR) {
            html += `<div class="wp2-hour-line" style="top:${y}px"></div>`;
        }
        if (h < TIMELINE_END_HOUR) {
            const halfY = y + 30 * PX_PER_MIN;
            html += `<div class="wp2-half-hour-line" style="top:${halfY}px"></div>`;
        }
    }
    return html;
}

function renderNowLine(): string {
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    if (nowMins < TIMELINE_START_HOUR * 60 || nowMins > TIMELINE_END_HOUR * 60) return '';
    const y = minsToY(nowMins);
    return `
        <div class="wp2-now-line" style="top:${y}px"></div>
        <div class="wp2-now-dot" style="top:${y}px"><span>Зараз ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}</span></div>
    `;
}

function renderFreeBlock(slot: FreeSlot): string {
    const rawStart = Math.max(slot.startMins, TIMELINE_START_HOUR * 60);
    const rawEnd = Math.min(slot.endMins, TIMELINE_END_HOUR * 60);
    if (rawEnd <= rawStart) return '';

    const top = minsToY(rawStart) + 1;
    const duration = rawEnd - rawStart;
    const height = Math.max(16, duration * PX_PER_MIN - 2);
    if (height < 16) return '';

    const showText = height >= 26;

    return `
    <div class="wp2-free-block" 
         style="top:${top}px;height:${height}px"
         onclick="openQuickAddModal(${slot.dayIndex}, '${minsToTime(rawStart)}', ${duration})"
         data-free-start="${rawStart}" data-free-end="${rawEnd}"
         data-tooltip="Вільне вікно: ${formatDuration(duration)} (${minsToTime(rawStart)}–${minsToTime(rawEnd)}). Натисніть, щоб додати задачу."
         data-tooltip-pos="right">
        ${showText ? `
            <div class="wp2-free-inner">
                <span class="wp2-free-text"><i class="ri-add-circle-line"></i> Вільний час — ${formatDuration(duration)}</span>
                <span class="wp2-free-time">${minsToTime(rawStart)}–${minsToTime(rawEnd)}</span>
            </div>
        ` : ''}
    </div>`;
}

function renderTaskBlock(task: PlannerTask): string {
    const rawStart = Math.max(timeToMins(task.startTime), TIMELINE_START_HOUR * 60);
    const rawEnd = Math.min(timeToMins(task.endTime), TIMELINE_END_HOUR * 60);
    if (rawEnd <= rawStart) return '';

    const top = minsToY(rawStart) + 1;
    const rawHeight = (rawEnd - rawStart) * PX_PER_MIN;
    const height = Math.max(24, rawHeight - 2);
    const isCompleted = task.status === 'completed';
    const isCompact = height < 58;
    const isVeryShort = height < 32;
    const icon = `<i class="${getCategoryIcon(task.category)}" style="color:${getCategoryColor(task.category)}"></i>`;
    const lock = task.isFixed ? '<i class="ri-lock-line wp2-fixed-icon" title="Фіксований блок"></i>' : '';

    const hasSplit = !task.isFixed && (task.allowSplit || task.durationMins >= 120);

    // Мінімалістичні дії: лише іконки, видно при наведенні
    const actions = task.isFixed ? '' : `
        <div class="wp2-block-actions ${hasSplit ? 'has-split' : ''}">
            <button class="wp2-block-action ${isCompleted ? 'is-active' : ''}" onclick="event.stopPropagation();toggleTaskCompleted('${task.id}')" data-tooltip="${isCompleted ? 'Відновити' : 'Виконано'}" data-tooltip-pos="top">
                <i class="${isCompleted ? 'ri-checkbox-circle-fill' : 'ri-check-line'}"></i>
            </button>
            <button class="wp2-block-action" onclick="event.stopPropagation();openFindSlotModal('${task.id}')" data-tooltip="Перенести на інший день" data-tooltip-pos="top">
                <i class="ri-calendar-event-line"></i>
            </button>
            ${hasSplit ? `
            <button class="wp2-block-action" onclick="event.stopPropagation();openSplitTaskModal('${task.id}')" data-tooltip="Розділити" data-tooltip-pos="top">
                <i class="ri-scissors-cut-line"></i>
            </button>` : ''}
            <button class="wp2-block-action" onclick="event.stopPropagation();moveTaskToDebt('${task.id}')" data-tooltip="Відкласти" data-tooltip-pos="top">
                <i class="ri-inbox-archive-line"></i>
            </button>
            <button class="wp2-block-action wp2-action-delete" onclick="event.stopPropagation();deletePlannerTask('${task.id}')" data-tooltip="Видалити слот" data-tooltip-pos="top">
                <i class="ri-delete-bin-line"></i>
            </button>
        </div>`;

    const resizeHandles = !task.isFixed ? `
        <div class="wp2-resize-top" data-resize-task="${task.id}" data-resize-edge="top"></div>
        <div class="wp2-resize-bottom" data-resize-task="${task.id}" data-resize-edge="bottom"></div>
    ` : '';

    const content = isCompact ? `
        <div class="wp2-block-row">
            <span class="wp2-block-title">${icon}<span>${task.title}</span>${lock}</span>
            <span class="wp2-block-time" data-role="time">${task.startTime}–${task.endTime}${isVeryShort ? '' : ` · ${formatDuration(task.durationMins)}`}</span>
        </div>
        ${actions}` : `
        <div class="wp2-block-title">${icon}<span>${task.title}</span>${lock}</div>
        <div class="wp2-block-time" data-role="time">${task.startTime}–${task.endTime} · ${formatDuration(task.durationMins)}</div>
        ${actions}`;

    return `
    <div class="wp2-task-block wp2-cat-${task.category} ${task.isFixed ? 'is-fixed' : 'is-movable'} ${isCompleted ? 'status-completed' : ''} ${isCompact ? 'is-compact' : ''} ${hasSplit ? 'has-split' : ''}"
         style="top:${top}px;height:${height}px"
         data-task-id="${task.id}"
         id="wp2-task-${task.id}">
        ${resizeHandles}
        ${content}
    </div>`;
}

function renderDaySidebar(dayIdx: number, stats: any): string {
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIdx && t.status !== 'needs_reschedule')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));
    const freeSlots = getFreeSlotsForDay(dayIdx).filter(s => s.startMins >= 540);

    // Delivery stats
    const deliveryStats = getWeekDeliverySummary();

    return `
    <!-- Day stats -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-bar-chart-box-line"></i> Баланс дня
        </div>
        <div class="wp2-sidebar-body">
            <div class="wp2-stats-grid">
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">💼 Робота</span>
                    <span class="wp2-stat-value">${stats.workH} год</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">🟢 Вільно</span>
                    <span class="wp2-stat-value" style="color:#34d399">${stats.freeH} год</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">🚗 Дорога</span>
                    <span class="wp2-stat-value">${stats.commuteH} год</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">🏠 Побут</span>
                    <span class="wp2-stat-value">${stats.routineH} год</span>
                </div>
            </div>
        </div>
    </div>

    <!-- Task list -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-list-check-3"></i> Задачі дня (${dayTasks.length})
        </div>
        <div class="wp2-sidebar-body">
            ${dayTasks.filter(t => t.category !== 'sleep').map(t => `
                <div class="wp2-side-task" draggable="${!t.isFixed}" data-task-id="${t.id}" onclick="scrollToTask('${t.id}')">
                    <div class="wp2-side-task-dot" style="background:${getCategoryColor(t.category)}"></div>
                    <div class="wp2-side-task-info">
                        <span class="wp2-side-task-name">${t.title}</span>
                        <span class="wp2-side-task-time">${t.startTime}–${t.endTime}</span>
                    </div>
                    <span class="wp2-side-task-dur">${formatDuration(t.durationMins)}</span>
                </div>
            `).join('')}
            ${dayTasks.filter(t => t.category !== 'sleep').length === 0 ? '<span style="font-size:11px;color:var(--text-dim);text-align:center;padding:12px">Немає задач</span>' : ''}
        </div>
    </div>

    <!-- Free slots -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-time-line" style="color:#34d399"></i> Вільні вікна (${freeSlots.length})
        </div>
        <div class="wp2-sidebar-body">
            ${freeSlots.map(s => `
                <div class="wp2-side-task" onclick="openQuickAddModal(${s.dayIndex}, '${s.startTime}', ${s.durationMins})" style="cursor:pointer">
                    <div class="wp2-side-task-dot" style="background:#34d399"></div>
                    <div class="wp2-side-task-info">
                        <span class="wp2-side-task-name" style="color:#34d399">Вільно</span>
                        <span class="wp2-side-task-time">${s.startTime}–${s.endTime}</span>
                    </div>
                    <span class="wp2-side-task-dur" style="color:#34d399">${formatDuration(s.durationMins)}</span>
                </div>
            `).join('')}
            ${freeSlots.length === 0 ? '<span style="font-size:11px;color:var(--text-dim);text-align:center;padding:12px">Немає вільних вікон після 09:00</span>' : ''}
        </div>
    </div>

    <!-- Weekly delivery -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-riding-line" style="color:#f97316"></i> Доставка тижня
        </div>
        <div class="wp2-sidebar-body">
            <div class="wp2-stats-grid">
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">Заплановано</span>
                    <span class="wp2-stat-value">${deliveryStats.planned.toFixed(1)}г</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">Квота</span>
                    <span class="wp2-stat-value">${deliveryStats.quota}г</span>
                </div>
            </div>
            ${deliveryStats.debt > 0 ? `<span style="font-size:11px;color:#fca5a5;display:flex;align-items:center;gap:4px"><i class="ri-alarm-warning-line"></i> Борг: ${deliveryStats.debt.toFixed(1)} год</span>` : ''}
        </div>
    </div>`;
}

// ==================== TIMELINE DRAG & DROP ====================
// Знаходить найближчий допустимий старт (магнітне прилипання до вільного місця)
function findNearestValidStart(dayIndex: number, desiredMins: number, durationMins: number, excludeId: string | null): number | null {
    const minStart = TIMELINE_START_HOUR * 60;
    const maxStart = 1440 - durationMins;
    const desired = Math.max(minStart, Math.min(maxStart, snapToGrid(desiredMins)));
    for (let offset = 0; offset <= 1440; offset += SNAP_MINS) {
        for (const cand of offset === 0 ? [desired] : [desired - offset, desired + offset]) {
            if (cand < minStart || cand > maxStart) continue;
            if (canPlaceTask(dayIndex, cand, durationMins, excludeId).ok) return cand;
        }
    }
    return null;
}

const DRAG_THRESHOLD_PX = 5;

function setupTimelineDragAndDrop() {
    const timeline = document.getElementById('wp2-timeline');
    const wrapper = document.getElementById('wp2-timeline-wrapper');
    if (!timeline) return;

    (timeline.querySelectorAll('.wp2-task-block.is-movable') as NodeListOf<HTMLElement>).forEach(block => {
        block.addEventListener('pointerdown', (e: PointerEvent) => {
            if (e.button !== 0) return;
            if ((e.target as HTMLElement).closest('.wp2-resize-top, .wp2-resize-bottom, button')) return;

            const task = tasks.find(t => t.id === block.dataset.taskId);
            if (!task || task.isFixed) return;

            dragStartY = e.clientY;
            dragOriginalStartMins = timeToMins(task.startTime);
            const startScroll = wrapper ? wrapper.scrollTop : 0;
            const timeEl = block.querySelector('[data-role="time"]') as HTMLElement | null;
            let started = false;
            let target: number | null = dragOriginalStartMins;
            let lastY = e.clientY;
            let scrollRaf = 0;

            const compute = () => {
                const scrollDelta = wrapper ? wrapper.scrollTop - startScroll : 0;
                const deltaMins = (lastY - dragStartY + scrollDelta) / PX_PER_MIN;
                const raw = Math.max(0, Math.min(1440 - task.durationMins, snapToGrid(dragOriginalStartMins + deltaMins)));
                block.style.top = `${minsToY(raw) + 1}px`;
                target = findNearestValidStart(task.dayIndex, raw, task.durationMins, task.id);
                updateDragPreview(timeline, target, task.durationMins);
                if (timeEl) timeEl.textContent = target !== null
                    ? `${minsToTime(target)}–${minsToTime(target + task.durationMins)}`
                    : 'Немає місця';
            };

            const autoScroll = () => {
                if (!wrapper || !started) return;
                const r = wrapper.getBoundingClientRect();
                const edge = 48;
                let dy = 0;
                if (lastY < r.top + edge) dy = -Math.ceil((r.top + edge - lastY) / 4);
                else if (lastY > r.bottom - edge) dy = Math.ceil((lastY - (r.bottom - edge)) / 4);
                if (dy !== 0) { wrapper.scrollTop += dy; compute(); }
                scrollRaf = requestAnimationFrame(autoScroll);
            };

            const onMove = (ev: PointerEvent) => {
                lastY = ev.clientY;
                if (!started) {
                    if (Math.abs(ev.clientY - dragStartY) < DRAG_THRESHOLD_PX) return;
                    started = true;
                    isDraggingOnTimeline = true;
                    draggedTaskId = task.id;
                    block.classList.add('is-dragging');
                    document.body.classList.add('wp2-no-select');
                    try { block.setPointerCapture(e.pointerId); } catch { /* noop */ }
                    scrollRaf = requestAnimationFrame(autoScroll);
                }
                ev.preventDefault();
                compute();
            };

            const finish = (commit: boolean) => {
                document.removeEventListener('pointermove', onMove);
                document.removeEventListener('pointerup', onUp);
                document.removeEventListener('pointercancel', onCancel);
                document.removeEventListener('keydown', onKey);
                cancelAnimationFrame(scrollRaf);
                document.body.classList.remove('wp2-no-select');
                const wasStarted = started;
                isDraggingOnTimeline = false;
                draggedTaskId = null;
                removeDragPreview();
                if (!wasStarted) return; // звичайний клік — нічого не рухаємо

                if (commit && target !== null && target !== dragOriginalStartMins) {
                    const prev = { start: task.startTime, end: task.endTime };
                    task.startTime = minsToTime(target);
                    task.endTime = minsToTime(target + task.durationMins);
                    saveScheduleData();
                    showPlannerToast(`«${task.title}» → ${task.startTime}–${task.endTime}`, 'success', () => {
                        task.startTime = prev.start;
                        task.endTime = prev.end;
                        saveScheduleData();
                        renderWeeklyPlanner();
                    });
                } else if (commit && target === null) {
                    showPlannerToast('Немає вільного місця такої тривалості', 'error');
                }
                renderWeeklyPlanner();
            };

            const onUp = () => finish(true);
            const onCancel = () => finish(false);
            const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') finish(false); };

            document.addEventListener('pointermove', onMove, { passive: false });
            document.addEventListener('pointerup', onUp);
            document.addEventListener('pointercancel', onCancel);
            document.addEventListener('keydown', onKey);
        });
    });
}

function updateDragPreview(timeline: HTMLElement, startMins: number | null, durationMins: number) {
    let preview = document.getElementById('wp2-drag-preview');
    if (startMins === null) { if (preview) preview.remove(); return; }
    if (!preview) {
        preview = document.createElement('div');
        preview.id = 'wp2-drag-preview';
        preview.className = 'wp2-drag-preview';
        timeline.appendChild(preview);
    }
    preview.style.top = `${minsToY(startMins) + 1}px`;
    preview.style.height = `${Math.max(22, durationMins * PX_PER_MIN - 2)}px`;
}

function removeDragPreview() {
    const p = document.getElementById('wp2-drag-preview');
    if (p) p.remove();
}

// ==================== TIMELINE RESIZE ====================
function setupTimelineResize() {
    const handles = document.querySelectorAll('.wp2-resize-top, .wp2-resize-bottom');

    handles.forEach(handle => {
        handle.addEventListener('mousedown', (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            const taskId = handle.getAttribute('data-resize-task');
            const edge = handle.getAttribute('data-resize-edge') as 'top' | 'bottom';
            const task = tasks.find(t => t.id === taskId);
            if (!task || task.isFixed) return;

            isResizing = true;
            resizeTaskId = taskId;
            resizeEdge = edge;
            resizeStartY = e.clientY;
            resizeOriginalMins = edge === 'top' ? timeToMins(task.startTime) : timeToMins(task.endTime);

            const onMouseMove = (ev: MouseEvent) => {
                if (!isResizing || !resizeTaskId) return;

                const deltaY = ev.clientY - resizeStartY;
                const deltaMins = snapToGrid(deltaY / PX_PER_MIN);

                const el = document.getElementById(`wp2-task-${task.id}`);
                if (!el) return;

                if (resizeEdge === 'top') {
                    const newStart = Math.max(0, resizeOriginalMins + deltaMins);
                    const endMins = timeToMins(task.endTime);
                    const newDuration = endMins - newStart;
                    if (newDuration >= 15 && newDuration <= 720) {
                        el.style.top = `${minsToY(newStart)}px`;
                        el.style.height = `${newDuration * PX_PER_MIN}px`;
                    }
                } else {
                    const startMins = timeToMins(task.startTime);
                    const newEnd = Math.min(1440, resizeOriginalMins + deltaMins);
                    const newDuration = newEnd - startMins;
                    if (newDuration >= 15 && newDuration <= 720) {
                        el.style.height = `${newDuration * PX_PER_MIN}px`;
                    }
                }
            };

            const onMouseUp = (ev: MouseEvent) => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);

                if (!isResizing || !resizeTaskId) return;

                const deltaY = ev.clientY - resizeStartY;
                const deltaMins = snapToGrid(deltaY / PX_PER_MIN);

                if (resizeEdge === 'top') {
                    const newStart = Math.max(0, resizeOriginalMins + deltaMins);
                    const endMins = timeToMins(task.endTime);
                    const newDuration = endMins - newStart;
                    if (newDuration >= 15 && newDuration <= 720) {
                        const check = canPlaceTask(task.dayIndex, newStart, newDuration, task.id);
                        if (check.ok) {
                            task.startTime = minsToTime(newStart);
                            task.durationMins = newDuration;
                            saveScheduleData();
                            showPlannerToast(`«${task.title}» → ${task.startTime}–${task.endTime} (${formatDuration(task.durationMins)})`, 'success');
                        } else {
                            showPlannerToast(check.reason || 'Конфлікт!', 'error');
                        }
                    }
                } else {
                    const startMins = timeToMins(task.startTime);
                    const newEnd = Math.min(1440, resizeOriginalMins + deltaMins);
                    const newDuration = newEnd - startMins;
                    if (newDuration >= 15 && newDuration <= 720) {
                        const check = canPlaceTask(task.dayIndex, startMins, newDuration, task.id);
                        if (check.ok) {
                            task.endTime = minsToTime(newEnd);
                            task.durationMins = newDuration;
                            saveScheduleData();
                            showPlannerToast(`«${task.title}» → ${task.startTime}–${task.endTime} (${formatDuration(task.durationMins)})`, 'success');
                        } else {
                            showPlannerToast(check.reason || 'Конфлікт!', 'error');
                        }
                    }
                }

                isResizing = false;
                resizeTaskId = null;
                resizeEdge = null;
                renderWeeklyPlanner();
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
    });
}

// ==================== WEEK DRAG & DROP (між днями) ====================
const DAY_DROP_SELECTOR = '.wp2-grid-col, .wp2-day-strip-item';

function setupWeekDragDrop() {
    // Debt cards + week-grid tasks draggable між днями
    const debtCards = document.querySelectorAll('.wp2-debt-card[draggable="true"], .wp2-grid-task[draggable="true"]');
    debtCards.forEach(card => {
        card.addEventListener('dragstart', (e: any) => {
            draggedTaskId = card.getAttribute('data-task-id');
            e.dataTransfer.setData('text/plain', draggedTaskId || '');
            e.dataTransfer.effectAllowed = 'move';
        });
        card.addEventListener('dragend', () => {
            draggedTaskId = null;
            clearWeekDropzones();
        });
    });

    // Week day drop zones
    const weekDays = document.querySelectorAll(DAY_DROP_SELECTOR);
    weekDays.forEach(col => {
        col.addEventListener('dragover', (e: any) => {
            e.preventDefault();
            if (!draggedTaskId) return;

            const dayIdx = Number(col.getAttribute('data-day-index'));
            const task = tasks.find(t => t.id === draggedTaskId);
            if (!task) return;

            const canFit = findNearestValidStart(dayIdx, timeToMins(task.startTime), task.durationMins, task.id) !== null;

            if (canFit) {
                col.classList.add('dropzone-valid');
                col.classList.remove('dropzone-invalid');
                e.dataTransfer.dropEffect = 'move';
            } else {
                col.classList.add('dropzone-invalid');
                col.classList.remove('dropzone-valid');
                e.dataTransfer.dropEffect = 'none';
            }
        });

        col.addEventListener('dragleave', (e: any) => {
            if (!col.contains(e.relatedTarget)) {
                col.classList.remove('dropzone-valid', 'dropzone-invalid');
            }
        });

        col.addEventListener('drop', (e: any) => {
            e.preventDefault();
            clearWeekDropzones();
            const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
            if (!taskId) return;
            const dayIdx = Number(col.getAttribute('data-day-index'));
            handleDropTaskOnDay(taskId, dayIdx);
        });
    });
}

function clearWeekDropzones() {
    document.querySelectorAll(DAY_DROP_SELECTOR).forEach(c => {
        c.classList.remove('dropzone-valid', 'dropzone-invalid');
    });
}

function handleDropTaskOnDay(taskId: string, dayIndex: number) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    if (task.isFixed) {
        showPlannerToast('Фіксовані задачі не можна переміщувати', 'warning');
        return;
    }

    // Намагаємось зберегти той самий час, інакше — найближче вільне місце
    const start = findNearestValidStart(dayIndex, timeToMins(task.startTime), task.durationMins, task.id);

    if (start === null) {
        showPlannerToast(`У ${DAYS_UK[dayIndex]} немає вікна на ${formatDuration(task.durationMins)}`, 'error');
        return;
    }

    task.dayIndex = dayIndex;
    task.startTime = minsToTime(start);
    task.endTime = minsToTime(start + task.durationMins);
    task.status = 'planned';

    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» → ${DAYS_UK[dayIndex]} (${task.startTime}–${task.endTime})`, 'success');
}

// ==================== СКРОЛ ДО ПОТОЧНОГО ЧАСУ ====================
function scrollToCurrentTime(instant: boolean = false) {
    const wrapper = document.getElementById('wp2-timeline-wrapper');
    if (!wrapper) return;
    const todayDayIndex = (new Date().getDay() + 6) % 7;
    let scrollTo = 0;
    if (selectedDayIndex === todayDayIndex) {
        const now = new Date();
        const nowMins = now.getHours() * 60 + now.getMinutes();
        if (nowMins >= TIMELINE_START_HOUR * 60) {
            scrollTo = Math.max(0, minsToY(nowMins) - 90);
        } else {
            const dayTasks = tasks.filter(t => t.dayIndex === selectedDayIndex && t.status !== 'needs_reschedule' && t.category !== 'sleep');
            if (dayTasks.length > 0) {
                const firstMins = Math.min(...dayTasks.map(t => timeToMins(t.startTime)));
                scrollTo = Math.max(0, minsToY(firstMins) - 30);
            } else {
                scrollTo = 0;
            }
        }
    } else {
        const dayTasks = tasks.filter(t => t.dayIndex === selectedDayIndex && t.status !== 'needs_reschedule' && t.category !== 'sleep');
        if (dayTasks.length > 0) {
            const firstMins = Math.min(...dayTasks.map(t => timeToMins(t.startTime)));
            scrollTo = Math.max(0, minsToY(firstMins) - 30);
        } else {
            scrollTo = 0;
        }
    }
    wrapper.scrollTop = scrollTo;
}

function scrollToTask(taskId: string) {
    const el = document.getElementById(`wp2-task-${taskId}`);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.outline = '2px solid var(--brand-primary)';
        setTimeout(() => { el.style.outline = 'none'; }, 1500);
    }
}

// ==================== GLOBAL FUNCTIONS ====================
(window as any).setPlannerView = function(view: 'week' | 'day') {
    currentView = view;
    renderWeeklyPlanner();
};

(window as any).selectDay = function(dayIdx: number, forceScroll: boolean = false) {
    if (selectedDayIndex === dayIdx && currentView === 'day' && !forceScroll) return;
    selectedDayIndex = dayIdx;

    const strip = document.getElementById('wp2-day-strip');
    const headerEl = document.getElementById('wp2-day-header');
    const timelineEl = document.getElementById('wp2-timeline');
    const timelineWrapper = document.getElementById('wp2-timeline-wrapper');

    // Якщо смужка днів і таймлайн уже на екрані — оновлюємо стан без перестворення DOM і без стрибків скролу
    if (currentView === 'day' && strip && headerEl && timelineEl && timelineWrapper) {
        const items = strip.querySelectorAll('.wp2-day-strip-item');
        items.forEach((item) => {
            const idx = Number((item as HTMLElement).dataset.dayIndex);
            if (idx === dayIdx) {
                item.classList.add('is-selected');
            } else {
                item.classList.remove('is-selected');
            }
        });

        const todayDayIndex = (new Date().getDay() + 6) % 7;

        // 1. Оновлюємо заголовок дня
        headerEl.innerHTML = renderDayHeaderContent(selectedDayIndex, todayDayIndex);

        // 2. Зберігаємо поточний скрол користувача, щоб таймлайн не скакав
        const savedScroll = timelineWrapper.scrollTop;

        // 3. Оновлюємо тільки вміст таймлайну (задачі та вільні слоти)
        timelineEl.dataset.dayIndex = String(selectedDayIndex);
        timelineEl.innerHTML = renderDayTimelineInner(selectedDayIndex, todayDayIndex);

        // 4. Фіксуємо скрол на тій самій позиції
        timelineWrapper.scrollTop = savedScroll;

        setupTimelineDragAndDrop();
        setupTimelineResize();

        if (forceScroll) {
            scrollToCurrentTime(true);
        }
        return;
    }

    currentView = 'day';
    renderWeeklyPlanner();
};

(window as any).navigateDay = function(delta: number) {
    // Тижневий шаблон повторюється: після неділі — понеділок, перед понеділком — неділя
    const nextDay = (((selectedDayIndex + delta) % 7) + 7) % 7;
    (window as any).selectDay(nextDay, false);
};

(window as any).scrollToTask = scrollToTask;

(window as any).toggleTaskCompleted = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.status = task.status === 'completed' ? 'planned' : 'completed';
    saveScheduleData();
    renderWeeklyPlanner();
};

(window as any).moveTaskToDebt = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.dayIndex = -1;
    task.status = 'needs_reschedule';
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» → у борг`, 'info');
};

(window as any).dismissDebtTask = function(taskId: string) {
    tasks = tasks.filter(t => t.id !== taskId);
    saveScheduleData();
    renderWeeklyPlanner();
    if (typeof (window as any).syncDeliveryWidgetFromPlanner === 'function') {
        (window as any).syncDeliveryWidgetFromPlanner();
    }
    showPlannerToast('Задачу видалено з боргу', 'info');
};

(window as any).deletePlannerTask = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    tasks = tasks.filter(t => t.id !== taskId);
    saveScheduleData();
    renderWeeklyPlanner();
    if (typeof (window as any).syncDeliveryWidgetFromPlanner === 'function') {
        (window as any).syncDeliveryWidgetFromPlanner();
    }
    showPlannerToast(`Слот «${task.title}» видалено`, 'info');
};

(window as any).applyBaseScheduleTemplate = function() {
    if (confirm('Скинути поточний тиждень та застосувати базовий шаблон графіка?')) {
        tasks = createDefaultSchedule();
        saveScheduleData();
        renderWeeklyPlanner();
        showPlannerToast('Базовий шаблон тижня застосовано', 'success');
    }
};

// ==================== МОДАЛКИ ====================
function closePlannerModal(id: string) {
    const m = document.getElementById(id);
    if (m) m.remove();
}
(window as any).closePlannerModal = closePlannerModal;

function openFindSlotModal(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const candidates = findAvailableSlots(task);

    const html = `
    <div class="wp2-modal-overlay" id="wp2-find-slot-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-search-eye-line" style="color:var(--brand-primary)"></i> Слоти для «${task.title}»</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-find-slot-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <div style="font-size:12px;color:var(--text-muted);display:flex;justify-content:space-between">
                    <span>Тривалість: <strong>${formatDuration(task.durationMins)}</strong></span>
                    <span>Знайдено: <strong>${candidates.length}</strong></span>
                </div>
                ${candidates.length === 0 ? `
                    <div style="text-align:center;padding:24px;color:#fca5a5">
                        <i class="ri-error-warning-line" style="font-size:28px;display:block;margin-bottom:6px"></i>
                        Немає вільного вікна на ${formatDuration(task.durationMins)}.
                        <br><span style="font-size:11px;color:var(--text-muted)">Спробуйте розділити задачу.</span>
                    </div>
                ` : candidates.map(c => `
                    <div class="wp2-candidate ${c.isPeak ? 'is-peak' : ''}">
                        <div class="wp2-candidate-info">
                            <span class="wp2-candidate-day">
                                ${c.dayName}
                                ${c.isPeak ? '<span class="wp2-tag wp2-tag-peak">⭐ Пік</span>' : ''}
                                ${c.isBuffer ? '<span class="wp2-tag wp2-tag-buffer">🌿 Буфер</span>' : ''}
                            </span>
                            <span class="wp2-candidate-time">${c.startTime} – ${c.endTime}</span>
                            <span class="wp2-candidate-desc">${c.description}</span>
                        </div>
                        <button class="wp2-btn wp2-btn-primary wp2-btn-sm" onclick="applyCandidateSlot('${task.id}', ${c.dayIndex}, '${c.startTime}', '${c.endTime}')">
                            Перенести
                        </button>
                    </div>
                `).join('')}
            </div>
        </div>
    </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
}
(window as any).openFindSlotModal = openFindSlotModal;

(window as any).applyCandidateSlot = function(taskId: string, dayIndex: number, startTime: string, endTime: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.dayIndex = dayIndex;
    task.startTime = startTime;
    task.endTime = endTime;
    task.status = 'planned';
    closePlannerModal('wp2-find-slot-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» → ${DAYS_UK[dayIndex]} (${startTime}–${endTime})`, 'success');
};

(window as any).openSplitTaskModal = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const half = Math.floor(task.durationMins / 2 / SNAP_MINS) * SNAP_MINS;

    const html = `
    <div class="wp2-modal-overlay" id="wp2-split-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-scissors-cut-line" style="color:var(--brand-primary)"></i> Розділити «${task.title}»</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-split-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <p style="font-size:12px;color:var(--text-muted)">
                    Загальна тривалість: <strong>${formatDuration(task.durationMins)}</strong>. Розбийте на 2 частини.
                </p>
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Частина 1 (хв)</label>
                        <input type="number" id="split-p1" class="wp2-form-input" value="${half}" step="${SNAP_MINS}" min="${SNAP_MINS}" max="${task.durationMins - SNAP_MINS}">
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Частина 2 (хв)</label>
                        <input type="number" id="split-p2" class="wp2-form-input" value="${task.durationMins - half}" step="${SNAP_MINS}" min="${SNAP_MINS}" max="${task.durationMins - SNAP_MINS}">
                    </div>
                </div>
                <div class="wp2-form-actions">
                    <button class="wp2-btn" onclick="closePlannerModal('wp2-split-modal')">Скасувати</button>
                    <button class="wp2-btn wp2-btn-primary" onclick="confirmSplitTask('${task.id}')">Розділити</button>
                </div>
            </div>
        </div>
    </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
};

(window as any).confirmSplitTask = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const p1 = Number((document.getElementById('split-p1') as HTMLInputElement)?.value) || 120;
    const p2 = Number((document.getElementById('split-p2') as HTMLInputElement)?.value) || (task.durationMins - p1);

    const baseTitle = task.title.replace(/ \(Частина \d+\)$/, '');

    task.durationMins = p1;
    task.title = `${baseTitle} (Частина 1)`;
    task.status = 'needs_reschedule';
    task.dayIndex = -1;

    const newTask: PlannerTask = {
        ...task,
        id: `task_${Date.now()}`,
        parentId: taskId,
        title: `${baseTitle} (Частина 2)`,
        durationMins: p2,
        dayIndex: -1,
        status: 'needs_reschedule'
    };

    tasks.push(newTask);
    closePlannerModal('wp2-split-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Задачу розділено: ${formatDuration(p1)} + ${formatDuration(p2)}`, 'success');
};

(window as any).rescheduleWeatherTask = function(dayIndex: number) {
    const deliveryTask = tasks.find(t => t.dayIndex === dayIndex && t.category === 'delivery');
    if (!deliveryTask) {
        showPlannerToast('Доставку на цей день не знайдено', 'info');
        return;
    }
    deliveryTask.dayIndex = -1;
    deliveryTask.status = 'needs_reschedule';
    activeWeatherAlert = null;
    saveScheduleData();
    renderWeeklyPlanner();
    openFindSlotModal(deliveryTask.id);
    showPlannerToast('Доставку знято через погоду', 'warning');
};

(window as any).autoRescheduleDebtTasks = function() {
    const debtTasks = tasks.filter(t => t.dayIndex === -1 || t.status === 'needs_reschedule');
    if (debtTasks.length === 0) {
        showPlannerToast('Всі задачі заплановані!', 'info');
        return;
    }
    let count = 0;
    for (const task of debtTasks) {
        const slots = findAvailableSlots(task);
        if (slots.length > 0) {
            const best = slots[0];
            task.dayIndex = best.dayIndex;
            task.startTime = best.startTime;
            task.endTime = best.endTime;
            task.status = 'planned';
            count++;
        }
    }
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Авто-розміщено ${count} задач`, 'success');
};

// Додавання задачі
(window as any).openQuickAddModal = function(dayIndex: number, defaultStart: string, maxMins: number) {
    const html = `
    <div class="wp2-modal-overlay" id="wp2-add-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-add-circle-line" style="color:var(--brand-primary)"></i> Додати задачу — ${DAYS_UK[dayIndex]}</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-add-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <div class="wp2-form-group">
                    <label class="wp2-form-label">Назва</label>
                    <input type="text" id="add-title" class="wp2-form-input" placeholder="Доставка, Спорт..." value="Доставка">
                </div>
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Початок</label>
                        <input type="time" id="add-start" class="wp2-form-input" value="${defaultStart}">
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Тривалість (хв)</label>
                        <input type="number" id="add-dur" class="wp2-form-input" value="${Math.min(240, maxMins)}" step="${SNAP_MINS}" max="${maxMins}">
                    </div>
                </div>
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Категорія</label>
                        <select id="add-cat" class="wp2-form-input" style="background:#18181b">
                            <option value="delivery">🚴 Доставка</option>
                            <option value="routine">🏠 Побут</option>
                            <option value="sport">🏋️ Спорт</option>
                            <option value="meal">🍽 Їжа</option>
                            <option value="school">🏫 Школа</option>
                            <option value="pum">💼 ПУМ</option>
                            <option value="other">📋 Інше</option>
                        </select>
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Тип</label>
                        <select id="add-fixed" class="wp2-form-input" style="background:#18181b">
                            <option value="0">Рухома</option>
                            <option value="1">Фіксована</option>
                        </select>
                    </div>
                </div>
                <div class="wp2-form-actions">
                    <button class="wp2-btn" onclick="closePlannerModal('wp2-add-modal')">Скасувати</button>
                    <button class="wp2-btn wp2-btn-primary" onclick="confirmAddTask(${dayIndex})">Додати</button>
                </div>
            </div>
        </div>
    </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
};

(window as any).openAddTaskModal = function() {
    const dayIdx = selectedDayIndex >= 0 ? selectedDayIndex : (new Date().getDay() + 6) % 7;
    const freeSlots = getFreeSlotsForDay(dayIdx).filter(s => s.startMins >= 540);
    const defaultSlot = freeSlots[0];
    const defaultStart = defaultSlot ? defaultSlot.startTime : '18:30';
    const maxMins = defaultSlot ? defaultSlot.durationMins : 240;
    (window as any).openQuickAddModal(dayIdx, defaultStart, maxMins);
};

(window as any).confirmAddTask = function(dayIndex: number) {
    const title = (document.getElementById('add-title') as HTMLInputElement)?.value.trim() || 'Нова задача';
    const startTime = (document.getElementById('add-start') as HTMLInputElement)?.value || '18:30';
    const durMins = Number((document.getElementById('add-dur') as HTMLInputElement)?.value) || 60;
    const cat = ((document.getElementById('add-cat') as HTMLSelectElement)?.value || 'delivery') as any;
    const isFixed = (document.getElementById('add-fixed') as HTMLSelectElement)?.value === '1';

    const startMins = timeToMins(startTime);
    const check = canPlaceTask(dayIndex, startMins, durMins);

    if (!check.ok) {
        showPlannerToast(`Не вдалося: ${check.reason}`, 'error');
        return;
    }

    const newTask: PlannerTask = {
        id: `task_${Date.now()}`,
        dayIndex,
        startTime,
        endTime: minsToTime(startMins + durMins),
        durationMins: durMins,
        title,
        category: cat,
        isFixed,
        allowSplit: !isFixed && cat === 'delivery',
        priority: 'medium',
        status: 'planned'
    };

    tasks.push(newTask);
    closePlannerModal('wp2-add-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${title}» → ${DAYS_UK[dayIndex]} (${newTask.startTime}–${newTask.endTime})`, 'success');
};

(window as any).openPeakSettingsModal = function() {
    const html = `
    <div class="wp2-modal-overlay" id="wp2-peak-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-settings-4-line" style="color:var(--brand-primary)"></i> Пікові години (Субота)</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-peak-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Початок піку</label>
                        <input type="time" id="peak-start" class="wp2-form-input" value="${plannerSettings.saturdayPeakStart}">
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Кінець піку</label>
                        <input type="time" id="peak-end" class="wp2-form-input" value="${plannerSettings.saturdayPeakEnd}">
                    </div>
                </div>
                <div class="wp2-form-actions">
                    <button class="wp2-btn" onclick="closePlannerModal('wp2-peak-modal')">Скасувати</button>
                    <button class="wp2-btn wp2-btn-primary" onclick="savePeakSettings()">Зберегти</button>
                </div>
            </div>
        </div>
    </div>`;
    document.body.insertAdjacentHTML('beforeend', html);
};

(window as any).savePeakSettings = function() {
    plannerSettings.saturdayPeakStart = (document.getElementById('peak-start') as HTMLInputElement)?.value || '17:00';
    plannerSettings.saturdayPeakEnd = (document.getElementById('peak-end') as HTMLInputElement)?.value || '22:00';
    closePlannerModal('wp2-peak-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast('Пікові години оновлено', 'success');
};

// ==================== TOAST ====================
function showPlannerToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', onUndo?: () => void) {
    if (onUndo) {
        document.getElementById('wp2-undo-toast')?.remove();
        const el = document.createElement('div');
        el.id = 'wp2-undo-toast';
        el.className = 'wp2-undo-toast';
        el.innerHTML = `<i class="ri-check-line"></i><span></span><button type="button">Скасувати</button>`;
        (el.querySelector('span') as HTMLElement).textContent = message;
        const timer = setTimeout(() => el.remove(), 5000);
        (el.querySelector('button') as HTMLButtonElement).onclick = () => {
            clearTimeout(timer);
            el.remove();
            onUndo();
        };
        document.body.appendChild(el);
        return;
    }
    if ((window as any).showMessage) {
        (window as any).showMessage(message, type);
    } else {
        console.log(`[${type}] ${message}`);
    }
}

export function syncTimelineHeightWithSidebar() {
    const timelineWrapper = document.getElementById('wp2-timeline-wrapper') as HTMLElement | null;
    if (timelineWrapper) {
        timelineWrapper.style.height = '';
        timelineWrapper.style.maxHeight = '';
    }
}

export function setupSidebarHeightSync() {
    window.addEventListener('resize', syncTimelineHeightWithSidebar);
}

// ==================== ІНІЦІАЛІЗАЦІЯ ====================
(window as any).renderWeeklyPlanner = renderWeeklyPlanner;
(window as any).loadScheduleData = loadScheduleData;
(window as any).syncTimelineHeightWithSidebar = syncTimelineHeightWithSidebar;

document.addEventListener('DOMContentLoaded', async () => {
    await loadScheduleData();
    loadWeatherAlert();
    setInterval(loadWeatherAlert, 60 * 60 * 1000);
    setupSidebarHeightSync();
});
